/* eslint-disable no-console */
import moment from 'moment';
import imageCompression from 'browser-image-compression';

const GHL_BASE    = 'https://services.leadconnectorhq.com';
const GHL_VERSION = '2021-07-28';

// In-memory cache folder id "kanban" — sama seperti cachedWmsFolderId di WMS
let cachedKanbanFolderId = null;

const getFileType = (file) => {
  const fileType = file.type.split('/')[0];
  return ['image', 'video', 'audio'].includes(fileType) ? fileType : 'file';
};

// Cari atau buat folder "kanban" di GHL Media Library
// Pattern exact sama dengan providers.ts yang sudah terbukti berjalan
const getKanbanFolderId = async (token, location) => {
  if (cachedKanbanFolderId) return cachedKanbanFolderId;

  try {
    // 1. Cari folder "kanban" yang sudah ada
    const listRes = await fetch(
      `${GHL_BASE}/medias/files?altId=${location}&altType=location&sortBy=createdAt&sortOrder=desc`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Version: GHL_VERSION,
          Accept: 'application/json',
        },
      }
    );

    if (listRes.ok) {
      const data = await listRes.json();
      const folder = (data?.files ?? data?.data ?? []).find(
        (f) => f.name === 'kanban' && !f.url
      );
      if (folder) {
        cachedKanbanFolderId = folder.id;
        return cachedKanbanFolderId;
      }
    }

    // 2. Buat folder baru — POST /medias/ exact sama dengan providers.ts
    const createRes = await fetch(`${GHL_BASE}/medias/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Version: GHL_VERSION,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        name: 'kanban',
        altId: location,
        altType: 'location',
      }),
    });

    if (createRes.ok) {
      const created = await createRes.json();
      cachedKanbanFolderId = created?.id ?? created?._id ?? null;
    }
  } catch (error) {
    console.error('[GHL] getKanbanFolderId error:', error);
  }

  // Kalau gagal buat folder, tetap lanjut upload ke root
  return cachedKanbanFolderId;
};

/**
 * Upload file ke GHL Media Library.
 *
 * Drop-in replacement untuk semua varian UploadFileToDropbox.
 * Return format sama persis: { link, type, size?, name?, ratioWidth?, ratioHeight? }
 *
 * @param {File}     file        - File object dari input
 * @param {string}   parentPath  - Tidak dipakai (kept for API compatibility)
 * @param {string}   _token      - Tidak dipakai (pakai REACT_APP_GHL_TOKEN)
 * @param {Function} setProgress - Callback progress 0-100
 */
const UploadFileToGHL = async (file, parentPath, _token, setProgress) => {
  const token    = process.env.REACT_APP_GHL_TOKEN;
  const location = process.env.REACT_APP_GHL_LOCATION;

  if (!token) {
    console.error('[GHL] REACT_APP_GHL_TOKEN is not set');
    return null;
  }
  if (!location) {
    console.error('[GHL] REACT_APP_GHL_LOCATION is not set');
    return null;
  }

  const currentMillis = moment().valueOf();
  const fileType      = getFileType(file);

  try {
    setProgress?.(10);

    // Compress image sama seperti UploadFileToAwsAndDropbox & V3
    let fileToUpload = file;
    if (fileType === 'image') {
      const options = {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };
      fileToUpload = await imageCompression(file, options);
    }

    setProgress?.(30);

    // Nama file: {timestamp}-{originalName}
    const filename = `${currentMillis}-${file.name}`;

    // Ambil folder id "kanban"
    const folderId = await getKanbanFolderId(token, location);

    setProgress?.(50);

    // Upload ke GHL media library
    const blob = new Blob([fileToUpload], { type: fileToUpload.type });
    const form = new FormData();
    form.append('file', blob, filename);
    form.append('hosted', 'false');
    form.append('name', filename);
    if (folderId) form.append('parentId', folderId);

    const uploadRes = await fetch(`${GHL_BASE}/medias/upload-file`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Version: GHL_VERSION,
        Accept: 'application/json',
      },
      body: form,
    });

    setProgress?.(90);

    if (!uploadRes.ok) {
      const detail = await uploadRes.text();
      console.error('[GHL] upload failed:', uploadRes.status, detail);
      return null;
    }

    const result = await uploadRes.json();
    const url    = result?.url ?? result?.fileUrl;

    if (!url) {
      console.error('[GHL] upload response missing url:', result);
      return null;
    }

    setProgress?.(100);

    // Ambil dimensi untuk image/video — sama seperti UploadFileToDropboxV2 & V3
    let ratioWidth  = 0;
    let ratioHeight = 0;
    const fileSize  = Math.round(file.size / (1024 * 1024)); // MB

    if (fileType === 'image') {
      const img = document.createElement('img');
      img.src   = URL.createObjectURL(file);
      await new Promise((resolve) => (img.onload = resolve));
      ratioWidth  = img.naturalWidth;
      ratioHeight = img.naturalHeight;
      URL.revokeObjectURL(img.src);
    } else if (fileType === 'video') {
      await new Promise((resolve) => {
        const video   = document.createElement('video');
        video.src     = URL.createObjectURL(file);
        video.onloadedmetadata = () => {
          ratioWidth  = video.videoWidth;
          ratioHeight = video.videoHeight;
          URL.revokeObjectURL(video.src);
          resolve();
        };
      });
    }

    // Return format sama persis dengan semua varian UploadFileToDropbox
    return {
      link: url,
      type: fileType,
      size: fileSize,
      name: filename,
      ratioWidth,
      ratioHeight,
    };

  } catch (error) {
    console.error('[GHL] UploadFileToGHL error:', error);
    return null;
  }
};

export default UploadFileToGHL;